import React, { Suspense } from 'react';
import i18n from '../i18n';

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

class LazyErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-sm text-gray-500">{i18n.t('Failed to load. Please try again.')}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm bg-primary text-white rounded"
          >
            {i18n.t('Reload')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// `fallback` lets a route show its own skeleton while the chunk downloads, instead of the generic spinner.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withLazy<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  fallback: React.ReactNode = <LazyFallback />
) {
  const LazyComponent = React.lazy(factory);
  const LazyWrapper = function LazyWrapper(props: React.ComponentProps<T>) {
    return (
      <LazyErrorBoundary>
        <Suspense fallback={fallback}>
          <LazyComponent {...props} />
        </Suspense>
      </LazyErrorBoundary>
    );
  };
  LazyWrapper.displayName = `Lazy(${factory.toString().match(/import\("\.\/(.+?)"\)/)?.[1] || 'Component'})`;
  return LazyWrapper;
}
